import React, { useRef, useEffect } from "react";
import type { QuaddieMarket } from "@/types/api/firestore/generated.zod";
import type { ProcessedSelection } from "@/hooks/firestore/betting/useEntries";
import { useEvent } from "@/hooks/firestore/betting/useEvent";
import { useQuaddieMarket } from "@/hooks/firestore/betting/useQuaddieMarkets";
import { useToggleSelection } from "@/features/betslip/hooks/useToggleSelection";
import { buildShareQuaddieInput } from "../../util/share-toggle-inputs";

type ShareQuaddieProps = {
  selections: ProcessedSelection[];
  onPickAddedToBetslip?: () => void;
  quaddieId: string;
  quaddieType?: QuaddieMarket["type"];
};

/**
 * Adds a shared quaddie to the betslip once its market document loads.
 */
const ShareQuaddie = (props: ShareQuaddieProps) => {
  const { selections, onPickAddedToBetslip, quaddieId, quaddieType } = props;
  const { toggle } = useToggleSelection();
  const hasBeenAdded = useRef(false);

  const { event } = useEvent(selections[0].event.eventId, "all");
  const { market } = useQuaddieMarket(quaddieId);

  useEffect(() => {
    if (!event || hasBeenAdded.current) return;
    if (market?.status !== "ACTIVE") return;

    // The single-doc hook doesn't stamp the firestore key the way the list
    // hook does, and the builder keys the slip entry on the market id.
    const input = buildShareQuaddieInput(
      event,
      { ...market, id: quaddieId },
      selections,
      quaddieType,
    );
    if (!input) return;

    toggle(input);
    onPickAddedToBetslip?.();
    hasBeenAdded.current = true;
  }, [selections, event, market, toggle]);

  return null;
};

export { ShareQuaddie };
export type { ShareQuaddieProps };
